import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import React, {useCallback, useEffect, useState} from 'react';
import {formatQuery, transformQuery} from 'react-querybuilder';
import {useSelector} from 'react-redux';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {RootState} from '../../../redux/store';
import {
  convertBetweenInRuleGroup,
  jsonLogicOperatorMap,
} from '../../../utils/queryBuilder';
import {parseQuery, unwrapQueryText} from '../../../utils/utils';
import AurButton from '../../common/aur-button/AurButton';
import AurDrawer from '../../common/aur-drawer/AurDrawer';
import AurIcon, {IconNames} from '../../common/aur-icon/AurIcon';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../constants/analyticsConstants';

interface IQueryFieldDrawerProps {
  open: boolean;
  setViewSyntax: (data: boolean) => void;
}

const ViewSyntaxDrawer: React.FC<IQueryFieldDrawerProps> = ({
  open,
  setViewSyntax,
}) => {
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;
  const {projectId} = useSelector((state: RootState) => state.reports);
  const {builder, index} = useSelector((state: RootState) => state.query);
  const [syntaxString, setSyntaxString] = useState<string>('');
  const {showToast} = useToast();
  const {trackGAEvent} = useAnalytics();

  const fetchRelationGroups = useCallback(async () => {
    const converted = convertBetweenInRuleGroup(builder);
    const jsonLogic = formatQuery(
      transformQuery(converted, {operatorMap: {...jsonLogicOperatorMap}}),
      'jsonlogic'
    );
    const result = parseQuery(jsonLogic);
    const updatedSyntaxString = unwrapQueryText(result);
    setSyntaxString(updatedSyntaxString);
  }, [index?.fieldTemplateId, clientId, projectId, builder]);

  const handleCopyToClipboard = () => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.queryBuilder.copySyntax'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.queryBuilder.copySyntaxAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.queryBuilder.viewSyntax'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
    navigator.clipboard.writeText(syntaxString).then(
      () =>
        showToast(
          t('queryBuilder.copiedToClipboard'),
          'success',
          undefined,
          'center'
        ),
      (err) => console.error('Failed to copy:', err)
    );
  };

  const closeDrawer = () => {
    setViewSyntax(false);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.close'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.DRAWER,
        action: t('gaTracking.queryBuilder.closedDrawer'),
      },
      subPage: {
        subpage_name: t('gaTracking.queryBuilder.viewSyntax'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  };

  useEffect(() => {
    trackGAEvent({
      eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
      subPage: {
        subpage_name: t('gaTracking.queryBuilder.viewSyntax'),
        subpage_type: GA_SUBPAGE_TYPE.DRAWER,
      },
    });
  }, []);

  useEffect(() => {
    if (!!projectId && clientId && index.name && builder.rules.length) {
      fetchRelationGroups();
    }
  }, [projectId, clientId, builder, index.name, fetchRelationGroups]);

  return (
    <AurDrawer
      className={'view-syntax-drawer client-drawer'}
      open={open}
      anchor={'right'}
      drawerHeader={
        <Box className="custom-field-drawer-header">
          <AurButton
            classNames="drawer-close-button"
            label={t('common.close')}
            type="exit"
            size="Medium"
            title={t('common.close')}
            onClick={() => closeDrawer()}
          />
        </Box>
      }
      drawerContent={
        <Box className="drawer-wrapper">
          <Box className="drawer-header">
            <Box>
              <Typography className="typo-text headline1_size">
                {t('queryBuilder.syntax')}
              </Typography>
              <Typography className="typo-text body1_size">
                {t('queryBuilder.dtSearch')}
              </Typography>
            </Box>
            <AurButton
              dataTestId={'copyToClipboardButton'}
              startIcon={<AurIcon icon={IconNames.ContentCopyOutlined} />}
              label={t('queryBuilder.copySyntax')}
              type={'text'}
              onClick={handleCopyToClipboard}
              size="Small"
            />
          </Box>

          <Box className="drawer-body">
            <Typography className="typo-text body2_size">
              {syntaxString}
            </Typography>
          </Box>
        </Box>
      }
    />
  );
};

export {ViewSyntaxDrawer};
